/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import {
  useSelectedOrganizationId,
  logout,
} from 'stores/useAuthStore';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import request from 'utils/request';
import { showSnackbar } from 'stores/useUiStore';

import type { ApiRespBody } from 'types/api';
import type { TSchema } from 'types/items';

import { TOKEN_INVALID } from './useBaseViewData';

export const SCHEMA_QUERY_KEY = ['baseViewSchema'] as const;

export async function fetchSchema(
  itemType: string,
  authToken: string,
  signal?: AbortSignal
): Promise<TSchema<any>> {
  const result = await request<ApiRespBody<TSchema<any>>>(
    `/api/${itemType}/schema`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', authorization: authToken },
      ...(signal ? { signal } : {}),
    }
  );

  if (result?.success && result.data) return result.data;
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || 'Failed to load schema');
}

export function baseViewSchemaQueryOptions(
  itemType: string,
  authToken: string,
  organizationId: number | string | null,
  enabled: boolean
) {
  return queryOptions({
    queryKey: [...SCHEMA_QUERY_KEY, itemType, organizationId],
    queryFn: ({ signal }) => fetchSchema(itemType, authToken, signal),
    staleTime: Infinity,
    enabled: enabled && !!authToken && !!itemType,
  });
}

export function useSchemaQuery(
  itemType: string,
  authToken: string,
  { enabled }: { enabled: boolean }
) {
  const queryClient = useQueryClient();
  const organizationId = useSelectedOrganizationId();

  const schemaQuery = useQuery(
    baseViewSchemaQueryOptions(itemType, authToken, organizationId, enabled)
  );

  useEffect(() => {
    if (!(schemaQuery.error instanceof Error)) return;
    if (schemaQuery.error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onSchemaRefresh',
          open: true,
          message: schemaQuery.error.message,
          severity: 'error',
        });
    }
  }, [schemaQuery.error]);

  const refetchSchema = () =>
    queryClient.invalidateQueries({
      queryKey: [...SCHEMA_QUERY_KEY, itemType],
    });

  return { schemaQuery, refetchSchema };
}
